import { ViewPlugin, EditorView, Decoration, keymap } from '@codemirror/view';
import { autocompletion, completionKeymap, CompletionContext, acceptCompletion } from '@codemirror/autocomplete';

/**
 * Term Autocomplete Extension for Claims Editor
 *
 * Provides intelligent term suggestions when user types "the" prefix.
 * Features:
 * - Triggers on "the" + space or partial term
 * - Frequency-based sorting
 * - Claim-scoped term relevance
 * - Fast, seamless UX
 */

/**
 * Format long text to show beginning and end instead of just beginning with ellipses
 * Uses variable length ellipses to avoid cutting words in half
 * @param {string} text - The text to format
 * @param {number} maxLength - Maximum length before truncation (default 50)
 * @param {number} endChars - Approximate number of characters to show at the end (default 15)
 * @returns {string} Formatted text
 */
function formatTruncatedText(text, maxLength = 50, endChars = 15) {
    if (!text || text.length <= maxLength) {
        return text;
    }

    const startChars = maxLength - endChars - 3; // 3 for "..."

    // Find the last complete word at the start
    let startText = text.slice(0, startChars);
    const lastSpaceInStart = startText.lastIndexOf(' ');
    if (lastSpaceInStart > 0) {
        startText = startText.slice(0, lastSpaceInStart);
    }

    // Find the first complete word at the end
    let endText = text.slice(-endChars);
    const firstSpaceInEnd = endText.indexOf(' ');
    if (firstSpaceInEnd > 0) {
        endText = endText.slice(firstSpaceInEnd + 1); // +1 to skip the space
    }

    return `${startText}...${endText}`;
}

class TermAutocompleteService {
    constructor(termsStore) {
        this.termsStore = termsStore;
        this.currentTerms = new Map();
        this.claimsContent = '';
        
        // Subscribe to terms store updates
        this.unsubscribe = termsStore.subscribe(store => {
            this.currentTerms = store.termEntries || new Map();
        });
    }
    
    /**
     * Get current claim number from cursor position
     */
    getCurrentClaim(state, pos) {
        const text = state.doc.toString();
        const beforeCursor = text.slice(0, pos);
        
        // Find the last claim number before cursor
        const claimMatches = [...beforeCursor.matchAll(/^(\d+)\./gm)];
        if (claimMatches.length === 0) return null;
        
        const lastClaimMatch = claimMatches[claimMatches.length - 1];
        return parseInt(lastClaimMatch[1]);
    }
    
    /**
     * Extract all terms (parent and child) with their frequencies
     */
    getAllTermsWithFrequency() {
        const termFrequency = new Map();
        
        // Process all term entries
        for (const [indexedKey, termData] of this.currentTerms) {
            const parentTerm = termData.parent_term;
            
            // Count parent term occurrences
            const parentCount = this.getTermOccurrenceCount(termData);
            termFrequency.set(parentTerm.toLowerCase(), {
                displayText: parentTerm,
                frequency: parentCount,
                type: 'parent',
                claimNum: termData.claim_num
            });
            
            // Count child terms
            if (termData.children && typeof termData.children === 'object') {
                for (const [childTerm, childData] of Object.entries(termData.children)) {
                    const childCount = childData.count || 1;
                    const existing = termFrequency.get(childTerm.toLowerCase());
                    
                    if (!existing || existing.frequency < childCount) {
                        termFrequency.set(childTerm.toLowerCase(), {
                            displayText: childTerm,
                            frequency: childCount,
                            type: 'child',
                            claimNum: termData.claim_num
                        });
                    }
                }
            }
        }
        
        return termFrequency;
    }
    
    /**
     * Get term occurrence count from term data
     */
    getTermOccurrenceCount(termData) {
        let count = 1; // Base occurrence
        
        // Add parent references
        if (termData.parent_references && termData.parent_references.claims) {
            count += termData.parent_references.claims.length;
        }
        
        return count;
    }
    
    /**
     * Check if term is relevant to current claim scope
     */
    isTermRelevantToScope(termInfo, currentClaim) {
        if (!currentClaim) return true; // No scope restriction if we can't determine claim
        
        // For now, include terms from current claim and previous claims
        // This allows users to reference terms from earlier claims
        return termInfo.claimNum <= currentClaim;
    }
    
    /**
     * Get completion suggestions for given prefix
     */
    getCompletions(context, prefix) {
        const currentClaim = this.getCurrentClaim(context.state, context.pos);
        const allTerms = this.getAllTermsWithFrequency();
        const suggestions = [];
        
        console.log(`[TermAutocomplete] Getting completions for prefix "${prefix}" in claim ${currentClaim}`);
        
        for (const [termKey, termInfo] of allTerms) {
            // Simple prefix matching on the original term (case insensitive)
            const termText = termInfo.displayText.toLowerCase();
            const lowerPrefix = prefix.toLowerCase();
            
            // Check if term matches prefix exactly
            if (prefix === '' || termText.startsWith(lowerPrefix)) {
                // Check scope relevance
                if (this.isTermRelevantToScope(termInfo, currentClaim)) {
                    suggestions.push({
                        label: formatTruncatedText(termInfo.displayText), // Format label to show beginning and end
                        apply: termInfo.displayText, // Use original term text for insertion
                        boost: termInfo.frequency, // Higher frequency = higher boost
                        type: termInfo.type
                    });
                }
            }
        }
        
        // Sort by frequency (boost) descending
        suggestions.sort((a, b) => b.boost - a.boost);
        
        console.log(`[TermAutocomplete] Found ${suggestions.length} suggestions:`, suggestions.slice(0, 5));
        
        return suggestions;
    }
    
    destroy() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }
}

/**
 * Create the autocomplete completion source
 */
function createTermCompletionSource(termsStore) {
    const service = new TermAutocompleteService(termsStore);
    
    return (context) => {
        // Get text before cursor to analyze the context
        const pos = context.pos;
        const lineStart = context.state.doc.lineAt(pos).from;
        const textBeforeCursor = context.state.doc.sliceString(lineStart, pos);
        
        // Look for the LAST "the " in the line - use a more specific approach
        // First check if they just typed "the" without space yet (at the very end)
        const theOnlyMatch = textBeforeCursor.match(/\bthe$/i);
        if (theOnlyMatch) {
            // They typed "the" - offer completions with empty prefix
            const suggestions = service.getCompletions(context, '');
            if (suggestions.length === 0) return null;
            
            // Modify each suggestion to include a space prefix
            const suggestionsWithSpace = suggestions.map(suggestion => ({
                ...suggestion,
                apply: ' ' + suggestion.apply // Add space before the term
            }));
            
            return {
                from: pos, // Insert at cursor position  
                options: suggestionsWithSpace,
                validFor: (text) => {
                    // Only show suggestions that actually start with the typed text
                    return suggestionsWithSpace.some(suggestion => 
                        suggestion.label.toLowerCase().startsWith(text.toLowerCase())
                    );
                }
            };
        }
        
        // Look for "the " followed by any text at the end (last occurrence)
        const theMatches = [...textBeforeCursor.matchAll(/\bthe\s+/gi)];
        if (theMatches.length === 0) return null;
        
        // Get the last "the " match
        const lastTheMatch = theMatches[theMatches.length - 1];
        const theStart = lastTheMatch.index;
        const theEnd = theStart + lastTheMatch[0].length;
        
        // Get everything after the last "the " as the prefix
        const afterThe = textBeforeCursor.substring(theEnd);
        const prefix = afterThe;
        
        console.log(`[TermAutocomplete] Found last "the " at position ${theEnd}, prefix: "${prefix}"`);
        
        const suggestions = service.getCompletions(context, prefix);
        if (suggestions.length === 0) return null;
        
        // Calculate the start position of the prefix part (after "the ")
        const prefixStart = lineStart + theEnd;
        
        return {
            from: prefixStart,
            options: suggestions,
            validFor: (text) => {
                // Only show suggestions that actually start with the typed text
                return suggestions.some(suggestion => 
                    suggestion.label.toLowerCase().startsWith(text.toLowerCase())
                );
            }
        };
    };
}

/**
 * Create the term autocomplete extension
 */
export function createTermAutocomplete(termsStore) {
    let completionService = null;
    
    return [
        // Service management plugin
        ViewPlugin.define(view => {
            completionService = new TermAutocompleteService(termsStore);
            return {
                destroy() {
                    if (completionService) {
                        completionService.destroy();
                        completionService = null;
                    }
                }
            };
        }),
        
        autocompletion({
            override: [createTermCompletionSource(termsStore)],
            closeOnBlur: true,
            activateOnTyping: true,
            maxRenderedOptions: 10, // Limit for performance
            defaultKeymap: true, // Keep default keymap for Enter, Escape, arrows
            activateOnCompletion: () => true // Always try to activate completion when source returns results
        }),
        
        // Override Tab to accept completion instead of cycling
        keymap.of([
            {
                key: "Tab",
                run: acceptCompletion
            }
        ]),
        
        // Custom theme for completion popup - styled like comments panel
        EditorView.theme({
            '.cm-tooltip-autocomplete': {
                background: 'var(--color-surface-panel-collab-frosted)',
                border: '1px solid var(--color-border)',
                borderRadius: '12px',
                boxShadow: 'var(--shadow-xl)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                fontSize: 'var(--font-size-sm)',
                maxHeight: '200px',
                overflow: 'auto',
                zIndex: '1000'
            },
            '.cm-completionOption': {
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer'
            },
            '.cm-completionOption:hover': {
                background: 'var(--color-terms-100)'
            },
            '.cm-completionOption[aria-selected]': {
                background: 'var(--color-terms-200)',
                color: 'var(--color-terms-800)'
            },
            '.cm-completionDetail': {
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-xs)',
                fontStyle: 'italic',
                marginLeft: '8px'
            },
            '.cm-completionLabel': {
                fontWeight: '500'
            }
        })
    ];
}